import React, { useState, useEffect, useRef } from 'react';
import { Card, CardBody, Container } from "reactstrap";
import { useParams, useNavigate, useLocation } from "react-router-dom";

//Import Breadcrumb
//import Breadcrumbs from "../../components/Common/Breadcrumb";

// import Component
import FolderTree from "./FolderTree";
import ItemsList from "./ItemsList";
import CSHAuth from "./CSHAuth";


// Checks token validity (runs before first render)
// Must be outside the component so it is defined before useState uses it
const getAuthStatus = () => {
  const token = localStorage.getItem('csh_jwt_token');
  if (!token) return false;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000 >= Date.now();
  } catch {
    return false;
  }
};

// get container height based on body data-layout attribute (vertical|horizontal)
// only needed to adjust the size when Clearnet theme layout is changed to vertical, as it has a taller header. If the layout is horizontal, we can use the full 80vh height since the header is shorter
// not needed for YMS integration since YMS doesn't have layout options - it will default to horizontal size
function getContainerHeight() {
  if (typeof document !== 'undefined') {
    const layout = document.body?.getAttribute('data-layout');
    if (layout === 'vertical') {
      return 'calc(90vh - 62px)'; // 80vh + 15% = 95vh
    }
  }
  return 'calc(80vh - 62px)';
}

const ClearStoneHub = () => {

  //meta title
  document.title = "ClearStone Hub | Clearnet ";

  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Compute getAuthStatus synchronously — children and data will not be loaded if false
  const [isAuthenticated, setIsAuthenticated] = useState(getAuthStatus);

  const folderTreeRef = useRef(); // Ref to call methods on FolderTree (eg forceRefresh)


  // Sidebar width state and drag logic
  const [sidebarWidth, setSidebarWidth] = useState(300); // initial width in px
  const minSidebarWidth = 180;
  const maxSidebarWidth = 500;
  const isDragging = useRef(false);
  const sidebarRef = useRef(null);


  // Sidebar drag handlers
  const handleMouseDown = (e) => {
    isDragging.current = true;
    document.body.style.cursor = 'col-resize';
  };

  // Sidebar mouse move and up handlers to resize the sidebar
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging.current) return;
      if (!sidebarRef.current) return;
      const sidebarLeft = sidebarRef.current.getBoundingClientRect().left;
      const newWidth = Math.min(Math.max(e.clientX - sidebarLeft, minSidebarWidth), maxSidebarWidth);
      setSidebarWidth(newWidth);
    };
    const handleMouseUp = () => {
      isDragging.current = false;
      document.body.style.cursor = '';
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);


  const handleForceRefresh = () => {
    if (folderTreeRef.current) {
      folderTreeRef.current.forceRefresh();
    }
  };


  // When a folder is changed, update the URL path
  const handleFolderChange = (nextId) => {
    // Normalize nextId and id for comparison
    const norm = v => v === undefined || v === null || v === '' ? null : String(v);
    const normNextId = norm(nextId);
    const normId = norm(id);
    if (normNextId === normId) return; // Prevent duplicate navigation
    if (normNextId) {
      navigate(`/ClearStoneHub/${normNextId}`);
    } else {
      navigate(`/ClearStoneHub`);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('csh_jwt_token');
    setIsAuthenticated(false);
  };

  ////////////////////////////////////////////
  // Authentication check on component mount
  ////////////////////////////////////////////
  // useEffect(() => {

  //   const token = localStorage.getItem('csh_jwt_token');
  //   if (!token) {
  //     navigate('/CSHAuth');
  //     return;
  //   }

  //   // check expiry if JWT token has expired
  //   try {

  //     const payload = JSON.parse(atob(token.split('.')[1]));
  //     if (payload.exp * 1000 < Date.now()) {
  //       localStorage.removeItem('csh_jwt_token');
  //       navigate('/CSHAuth');
  //     }
  //   } catch (e) {
  //     localStorage.removeItem('csh_ jwt_token');
  //     navigate('/CSHAuth');
  //   }

  // }, [navigate]);

  return (
    <React.Fragment>
      <div className="page-content" >
        <Container fluid style={{ height: getContainerHeight(), minHeight: 0, ...(process.env.REACT_APP_WEBSITE === 'YMS' && { paddingTop: '24px', paddingBottom: '24px' }) }}>
          <div style={{ display: 'flex', height: '100%', minHeight: 0 }}>
            <div className="w-100" style={{ height: '100%', minHeight: 0 }}>
              <Card style={{ height: '100%', minHeight: 0, ...(process.env.REACT_APP_WEBSITE === 'YMS' && { border: '1px solid #dee2e6', borderRadius: '4px' }) }}>
                <CardBody style={{ padding: 0, height: '100%', minHeight: 0 }}>

                  {/* Show auth form inside the card if not authenticated */}
                  {!isAuthenticated ? (
                    <CSHAuth onAuthenticated={() => setIsAuthenticated(true)} />
                  ) : (
                    <div style={{
                      display: 'flex',
                      alignItems: 'stretch',
                      width: '100%',
                      gap: 0,
                      boxSizing: 'border-box',
                      height: '100%',
                      minHeight: 0
                    }}>
                      <div
                        ref={sidebarRef}
                        style={{
                          flex: `0 0 ${sidebarWidth}px`,
                          display: 'flex',
                          flexDirection: 'column',
                          height: '100%',
                          minHeight: 0,
                          padding: 0,
                          boxSizing: 'border-box',
                        }}
                      >
                        <FolderTree ref={folderTreeRef} currentId={id} onFolderChange={handleFolderChange} sidebarWidth={sidebarWidth} minSidebarWidth={minSidebarWidth} maxSidebarWidth={maxSidebarWidth} />
                      </div>
                      {/* Draggable Divider */}
                      <div
                        style={{
                          width: 6,
                          cursor: 'col-resize',
                          background: '#e0e0e0',
                          zIndex: 2,
                          userSelect: 'none',
                          height: '100%',
                        }}
                        onMouseDown={handleMouseDown}
                      />
                      <div style={{
                        flex: 1,
                        minWidth: 0,
                        boxSizing: 'border-box',
                        padding: '24px',
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%',
                        minHeight: 0
                      }}>
                        <ItemsList
                          parentId={id}
                          onFolderChange={handleFolderChange}
                          onForceRefresh={handleForceRefresh}
                          onLogout={handleLogout}
                        />
                      </div>
                    </div>
                  )}

                </CardBody>
              </Card>
            </div>
          </div>
        </Container>
      </div>
    </React.Fragment>
  )
}
export default ClearStoneHub
